import { Button, Container, makeStyles, Typography } from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import { medicalPinState, storedMedicalPinState } from 'pages/CustomerBooking/RecoilState';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PinInput } from 'react-input-pin-code';
import ReactJson from 'react-json-view';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { HelvNeue } from 'themes/theme';
import { useTrackEvent } from 'util/trackEvent';

import { useStyles as baseStyles } from '../styles';
const useStyles = makeStyles((theme) => {
  return {
    messageContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      height: '100%',
      "&>*": {
        marginBottom: theme.spacing(3)
      }
    },
    pinContainer: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      height: '100%',
      padding: theme.spacing(4),
      "&>*": {
        marginBottom: theme.spacing(3)
      }
    },
    snek: {
      height: theme.spacing(20),
      width: 'auto'
    },
    large: {
      fontFamily: HelvNeue,
      fontWeight: 300
    }
  }
})


const MessagePinView = ({ onDismissMessage = () => { } }) => {
  const classes = { ...useStyles(), ...baseStyles() }
  const trackEvent = useTrackEvent();
  return (
    <Container maxWidth="xs" className={classes.messageContainer}>
      <img src='/assets/images/caduceus.svg' alt='pin' className={classes.snek} />
      <Typography variant='h2' align="center">Medical Privacy Enabled Chat</Typography>
      <Typography variant='body1' align='center'>For added security, we will require a PIN Authentication everytime you open your medical chat</Typography>
      <Button fullWidth onClick={() => {
        onDismissMessage()
        trackEvent('Chat - Medical Privacy click - OK', {})
      }}
        variant='contained' color='primary' size='large'>OK</Button>
    </Container>
  )
}

const NewPinView = ({ isInvalid = false, onNewPin = () => { } }) => {
  const classes = { ...useStyles(), ...baseStyles() }
  const [newPinCode, setNewPinCode] = useState(new Array(4).fill(''));
  

  return (
    <Container className={classes.pinContainer}>
      <Typography variant='h2' align="center" className={classes.large}>{isInvalid ? 'Invalid PIN code, please try again' : 'Please enter your medical chat PIN'}</Typography>
      <PinInput
        autoFocus
        values={newPinCode}
        size="lg"
        placeholder=""
        onChange={(char, index, pinString) => {
          setNewPinCode(pinString)
        }}
        onComplete={(v) => {
          
          setNewPinCode(new Array(4).fill(''))
          onNewPin(v.join(''))
        }} />
    </Container>
  )
}


const ConfirmPinView = ({ newPin = "", onConfirmPin = () => { }, onResetPin = () => { } }) => {
  const classes = { ...useStyles(), ...baseStyles() }
  const [confirmPinCode, setConfirmPinCode] = useState(new Array(4).fill(''));

  const trackEvent = useTrackEvent();

  const comparePins = useCallback(
    (confirmPin) => {
      if (newPin === confirmPin) {
        onConfirmPin(confirmPin)
      } else {
        onResetPin()
      }
    },
    [newPin, onConfirmPin, onResetPin],
  );

  return (
    <Container className={classes.pinContainer}>
      <Typography variant='h2' align="center" className={classes.large}>Great!  Now, please confirm your PIN </Typography>
      <PinInput
        autoFocus
        values={confirmPinCode}
        size="lg"
        placeholder=""
        onChange={(char, index, pinString) => {
          setConfirmPinCode(pinString)
        }}
        onComplete={(v) => {
          setConfirmPinCode(new Array(4).fill(''))
          comparePins(v.join(''))
        }} />
    </Container>
  )
}



const InputPinView = ({ storedPin, onComplete = () => { } }) => {
  const classes = { ...useStyles(), ...baseStyles() }
  const [confirmPinCode, setConfirmPinCode] = useState(new Array(4).fill(''));
  const [invalid, setInvalid] = useState(false);
  const [resetComponentKey, setResetComponentKey] = useState(0);
  const trackEvent = useTrackEvent();
  
  const comparePins = useCallback(
    (confirmPin) => {
      setInvalid(false)
      if (storedPin === confirmPin) {
        onComplete()
      } else {
        trackEvent('Chat - Medical Privacy - Invalid PIN', {})
        setConfirmPinCode(new Array(4).fill(''))
        setResetComponentKey(Math.random())
        setInvalid(true)
      }
    },
    [onComplete, storedPin],
  );

  return (
    <Container className={classes.pinContainer}>
      <Typography variant='h2' align="center" className={classes.large}>{invalid ? 'Invalid PIN code, please try again' : 'Please enter your medical chat PIN'}</Typography>
      <PinInput
        key={resetComponentKey}
        autoFocus
        values={confirmPinCode}
        size="lg"
        placeholder=""
        onChange={(char, index, pinString) => {
          setConfirmPinCode(pinString)
        }}
        onComplete={(v) => {
          comparePins(v.join(''))
        }} />
    </Container>
  )
}

const PinView = ({ }) => {
  const classes = { ...useStyles(), ...baseStyles() }
  const storedPin = useRecoilValue(storedMedicalPinState)
  const currentPin = useRecoilValue(medicalPinState)
  const setStoredPin = useSetRecoilState(storedMedicalPinState)
  const setCurrentPin = useSetRecoilState(medicalPinState)

  const [newPin, setNewPin] = useState();
  const [newConfirmPin, setNewConfirmPin] = useState();

  const [messageDismissed, setMessageDismissed] = useState(false);
  const [invalidPin, setInvalidPin] = useState(false);
  const trackEvent = useTrackEvent();

  const PinTypeView = useMemo(() => {
    if (!messageDismissed) {
      return <MessagePinView onDismissMessage={() => setMessageDismissed(true)} />
    }

    if (isEmpty(currentPin) && !isEmpty(storedPin)) {
      return <InputPinView
        storedPin={storedPin}
        onComplete={() => {
          trackEvent('Chat - Medical Privacy - Set PIN', {})
          setCurrentPin(storedPin)
          setInvalidPin(false)
        }}
      />
    } else if (!newPin && isEmpty(storedPin)) {
      return <NewPinView
        isInvalid={invalidPin}
        onNewPin={(pin) => {
          trackEvent('Chat - Medical Privacy - Set New PIN', {})
          setNewPin(pin)
          setInvalidPin(false)
        }} />
    } else if (newPin && isEmpty(storedPin)) {
      return <ConfirmPinView
        newPin={newPin}
        onConfirmPin={(pin) => {
          trackEvent('Chat - Medical Privacy - Confirm PIN', {})
          setNewConfirmPin(pin)
          setStoredPin(pin)
        }}
        onResetPin={() => {
          trackEvent('Chat - Medical Privacy - Reset PIN', {})
          setNewPin()
          setInvalidPin(true)
        }}
      />
    }
  }, [currentPin, invalidPin, messageDismissed, newPin, setCurrentPin, setStoredPin, storedPin]);

  return (
    <div className={classes.chatContainer}>
      {PinTypeView}
    </div>
  )
}

export { PinView }